import { BulletListItem } from "@sb1/ffe-lists-react";
import type { ReactNode } from "react";
import { FormattedMessage, createIntl, createIntlCache } from "react-intl";
import { Link } from "../components/link";

const cache = createIntlCache();
const createLinkRenderer = (href: string) => (chunks: ReactNode[]) => <Link to={href}>{chunks}</Link>;

export const fellesIntl = createIntl(
	{
		locale: "no",
		messages: {},
		onError: () => undefined, // Dette stopper oss fra å få masse feilmeldinger i console mens vi ikke har oversettelser
	},
	cache,
);

export const fellesIntlJa = fellesIntl.formatMessage({
	id: "ja",
	description: "Brukt i ja/nei spørsmål",
	defaultMessage: "Ja",
});
export const fellesIntlNei = fellesIntl.formatMessage({
	id: "nei",
	description: "Brukt i ja/nei spørsmål",
	defaultMessage: "Nei",
});

export const FellesIntlJa = () => <FormattedMessage id="ja" description="Brukt i ja/nei spørsmål" defaultMessage="Ja" />;
export const FellesIntlNei = () => <FormattedMessage id="nei" description="Brukt i ja/nei spørsmål" defaultMessage="Nei" />;

export const FellesIntlTilbake = () => <FormattedMessage id="tilbake" description="Tilbake knapp" defaultMessage="Tilbake" />;
export const FellesIntlGaVidere = () => <FormattedMessage id="gaVidere" description="Neste knapp" defaultMessage="Gå videre" />;
export const FellesIntlSlett = () => <FormattedMessage id="slett" description="Slett knapp" defaultMessage="Slett" />;
export const FellesIntlAvbryt = () => <FormattedMessage id="avbryt" description="Knapp for å avbryte et skjema" defaultMessage="Avbryt" />;
export const FellesIntlLagre = () => <FormattedMessage id="lagre" description="Knapp for å lagre en endring" defaultMessage="Lagre" />;
export const FellesIntlEndre = () => <FormattedMessage id="endre" description="Knapp for å åpne et skjema for å endre et valg" defaultMessage="Endre" />;

export const FellesIntlValgfritt = () => <FormattedMessage id="valgfritt" description="Tekst som informerer brukeren om at feltet er valgfritt å fylle ut" defaultMessage="Valgfritt" />;

export const FellesIntlArligPaminnelse = () => (
	<FormattedMessage
		id="arlig-paminnelse.checkbox.tekst"
		description="Teskt for en checkbox der man kan godkjenne å få årlig påminnelse for investeringsvalg"
		defaultMessage="
            Jeg ønsker en årlig påminnelse om veiledning for å
            sørge for at pensjonen min er investert i henhold til mine preferanser.
        "
	/>
);

export const FellesIntlLesMer = () => <FormattedMessage id="lesMer" description="Knapp for å lese mer" defaultMessage="Les mer" />;

export const FellesIntlBulletItem = (str) => <BulletListItem>{str}</BulletListItem>;

const mer = fellesIntl.formatMessage({
	id: "visMerMindre.mer",
	description: "Del av Vis mer/mindre",
	defaultMessage: "mer",
});
const mindre = fellesIntl.formatMessage({
	id: "visMerMindre.mindre",
	description: "Del av Vis mer/mindre",
	defaultMessage: "mindre",
});
export const FellesIntlVisMerMindre = ({ vis }: { vis: boolean }) => (
	<FormattedMessage id="visMerMindre" description="Vis mer/mindre knapp" defaultMessage="Vis {merMindre}" values={{ merMindre: vis ? mindre : mer }} />
);

export const FellesIntlKid = () => <FormattedMessage id="kid" description="Tekst før kid" defaultMessage="KID:" />;
export const FellesIntlKontonummer = () => <FormattedMessage id="kontonummer" description="Tekst før kontonummer" defaultMessage="Kontonummer:" />;

export const fellesIntlErrorSvarPaSporsmalet = fellesIntl.formatMessage({
	id: "error.svar-pa-sporsmalet",
	description: "Error melding hvis et spørsmål ikke blir svart",
	defaultMessage: "Du må svare på dette spørsmålet",
});

export const fellesIntlErrorPakrevd = fellesIntl.formatMessage({
	id: "error.pakrevd",
	description: "Error melding hvis en checkbox er påkrevd",
	defaultMessage: "Dette feltet er påkrevd",
});

export const fellesIntlErrorJaNei = fellesIntl.formatMessage({
	id: "error.ja-nei",
	description: "Error melding for ja/nei spørsmål",
	defaultMessage: "Du må svare ja eller nei",
});

export const fellesIntlErrorVelgAlternativ = fellesIntl.formatMessage({
	id: "error.velg-alternativ",
	description: "Error melding når man må velge et alternativ",
	defaultMessage: "Du må velge et av alternativene",
});

export const fellesIntlErrorBelop = fellesIntl.formatMessage({
	id: "error.belop",
	description: "Error melding når man må legge til et beløp",
	defaultMessage: "Fyll inn et beløp",
});

export const FellesIntlLukk = () => <FormattedMessage id="lukk" description="Knapp for å lukke noe" defaultMessage="Lukk" />;

export const FellesIntlGaTilDinOversikt = () => (
	<FormattedMessage id="gaa-til-din-oversikt" description="Tekst til knappen som tar deg til oversiktssiden" defaultMessage="Gå til din oversikt" />
);

export const FellesIntlExternalLinkHer = (href: string) => (
	<FormattedMessage
		id="link til ekstern side med 'her' som tekst"
		description="Tekst til knappen som tar deg til oversiktssiden"
		defaultMessage="<LinkComp>her</LinkComp>"
		values={{ LinkComp: createLinkRenderer(href) }}
	/>
);

export const FellesIntlMinPensjon = () => <FormattedMessage id="min-pensjon" description="Knapp for å gå til Min pensjon" defaultMessage="Min pensjon" />;

export const FellesIntlForrige = () => <FormattedMessage id="forrige" description="Knapp som går tilbake" defaultMessage="Forrige" />;

export const fellesIntlJoinOg = fellesIntl.formatMessage({
	id: "join.og",
	description: "Brukt for å koble sammen en liste med navn",
	defaultMessage: "og $1",
});
